import React from 'react';
import PropTypes from 'prop-types';

// components
import FolderIcon from 'dibs-vg/dist/react/folder';
import HeartIcon from 'dibs-vg/dist/react/heart-filled';
import { isDesignerConnectionMessageVisible } from 'dibs-buyer-layout/exports/DesignerConnection';
import { HeartWrapperLoader } from '../../../global/HeartWrapperLoader';
import { FolderWrapperLoader } from '../../../global/FolderWrapperLoader';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { PdpSharedSourcingModalLoaderWrapper } from '../../../pdp/PdpSharedModals/PdpSharedSourcingModal/PdpSharedSourcingModalLoaderWrapper';
import { PdpRespTradeSourcingPromptModal } from '../../../pdp/PdpRespTradeSourcingPromptModal/PdpRespTradeSourcingPromptModal';
import {
    hasSeenFavoritesTooltip,
    logFavoritesTooltipView,
    logTradeSourcingPromptModalView,
    canShowTradeSourcingPromptModal,
} from './localStorageHelpers';
import { ServerVarsContextConsumer } from '../../../global/ServerVarsContext/ServerVarsContext';

const TradeFavoritesTooltip = React.lazy(() =>
    import(
        /* webpackChunkName: "TradeFavoritesTooltip" */ '../TradeFavoritesTooltip/TradeFavoritesTooltip'
    )
);

// styles
import styles from './FavoriteIcons.scss';

// helpers
import {
    locations,
    authLabels,
    folderTrackingHooks,
    trackHeartFavorited,
    trackHeartUnfavorited,
} from '../../../utils/tracking/shared/favoritesTracking';
import { loadAddToFolderModalWrapper } from '../../../global/loadAddToFolderModalWrapper';
import classNames from 'classnames';
import {
    SAVE_ITEM_FLOW,
    SAVE_ITEM_TO_FOLDER_FLOW,
    sharedLoginCheck,
} from '../../../authentication/sharedLoginCheck';
import { DesignerConnectionFolderConfirmationModal } from './DesignerConnectionFolderConfirmationModal';
import {
    trackFavoriteTooltipClicked,
    trackFavoriteTooltipSourcingHelpClicked,
} from '../TradeFavoritesTooltip/tradeFavoritesTooltipTracking';

export class FavoritesItem extends React.Component {
    constructor(props) {
        super(props);

        this.showAuthModal = this.showAuthModal.bind(this);
        this.getFolderModalComponent = this.getFolderModalComponent.bind(this);
        this.onAddItemSuccess = this.onAddItemSuccess.bind(this);
        this.checkIfShowTooltip = this.checkIfShowTooltip.bind(this);
        this.openSourcingModal = this.openSourcingModal.bind(this);
        this.closeSourcingModal = this.closeSourcingModal.bind(this);
        this.onFavorited = this.onFavorited.bind(this);
        this.closeTooltip = this.closeTooltip.bind(this);

        this.folderTrackingHooks = this.getFolderTrackingHooks(props);

        this.savedAuthState = null;
        this.state = {
            showFolderConfirmation: false,
            portfolioName: '',
            showTooltip: false,
            isModalOpen: false,
            isTradeSourcingPromptModalOpen: false,
        };
    }

    onAddItemSuccess({ portfolioName }) {
        const { isVerifiedTrade } = this.props;
        if (isVerifiedTrade && canShowTradeSourcingPromptModal()) {
            this.setState({ isTradeSourcingPromptModalOpen: true });
        } else if (isVerifiedTrade && isDesignerConnectionMessageVisible()) {
            this.setState({ showFolderConfirmation: true, portfolioName });
        } else if (authModalLoader.isPaused()) {
            authModalLoader.resume();
        }
    }

    getFolderTrackingHooks(props) {
        let hooks = folderTrackingHooks;

        if (props.location) {
            hooks = Object.keys(folderTrackingHooks).reduce((map, current) => {
                map[current] = folderTrackingHooks[current].bind(null, props.location);
                return map;
            }, {});
        }

        return hooks;
    }

    showAuthModal({ flow, onSuccess, onError, shouldPauseAuthFlow } = {}) {
        const { itemImage, itemTitle } = this.props;

        sharedLoginCheck({
            onSuccess,
            onError,
            authOptions: {
                flow,
                shouldPauseAuthFlow,
                ga: { label: authLabels[this.props.location] },
                additionalProps: { item: { firstPhotoWebPath: itemImage, title: itemTitle } },
            },
        });
    }

    getFolderModalComponent() {
        return new Promise((resolve, reject) => {
            this.showAuthModal({
                flow: SAVE_ITEM_TO_FOLDER_FLOW,
                shouldPauseAuthFlow: state => state.step === 0,
                onSuccess: ({ userId }) => {
                    return loadAddToFolderModalWrapper({ userId }).then(resolve);
                },
                onError: reject,
            });
        });
    }

    renderHeart() {
        if (this.props.disable) {
            return <HeartIcon className={styles.icon} />;
        }

        return (
            <HeartWrapperLoader
                load
                itemId={this.props.itemId}
                userId={this.props.userId}
                viewer={this.props.viewer}
                authModalShow={cb => this.showAuthModal({ flow: SAVE_ITEM_FLOW, onSuccess: cb })}
                onFavorited={this.onFavorited}
                onUnFavorited={() => trackHeartUnfavorited(this.props.location)}
                className={this.props.heartIconClass}
            />
        );
    }

    onFavorited() {
        trackHeartFavorited(this.props.location);
        this.checkIfShowTooltip();
    }

    renderFolder() {
        if (this.props.disable) {
            return <FolderIcon className={styles.icon} />;
        }

        return (
            <FolderWrapperLoader
                load
                userId={this.props.userId}
                userIds={this.props.userIds}
                primaryItemPk={this.props.itemId}
                selectedItemIds={this.props.itemIds}
                singlePortfolioItem={this.props.singlePortfolioItem}
                loadPortfolioItemData={this.props.loadPortfolioItemData}
                viewer={this.props.viewer}
                environment={this.props.environment}
                trackingHooks={this.folderTrackingHooks}
                getModalComponent={this.getFolderModalComponent}
                additionalIconClass={this.props.folderIconClass}
                onAddItemSuccess={this.onAddItemSuccess}
                shouldFillInFolder={!!this.props.userId}
            />
        );
    }

    checkIfShowTooltip() {
        const { showTooltipOnFirstClick, isVerifiedTrade, tooltipTrackingLabel } = this.props;

        if (showTooltipOnFirstClick && !hasSeenFavoritesTooltip() && isVerifiedTrade) {
            this.setState({ showTooltip: true });
            logFavoritesTooltipView();
            trackFavoriteTooltipClicked(tooltipTrackingLabel);
        }
    }

    openSourcingModal(e) {
        e.preventDefault();
        this.setState({ isModalOpen: true, showTooltip: false });
        trackFavoriteTooltipSourcingHelpClicked(this.props.tooltipTrackingLabel);
    }

    closeSourcingModal() {
        this.setState({ isModalOpen: false });
    }

    closeTooltip() {
        this.setState({ showTooltip: false });
    }

    render() {
        const {
            showFolderConfirmation,
            portfolioName,
            showTooltip,
            isModalOpen,
            isTradeSourcingPromptModalOpen,
        } = this.state;
        const {
            isVerifiedTrade,
            itemTitle,
            itemId,
            fetchHeart,
            fetchFolder,
            heartIconWrapperClass,
            folderIconWrapperClass,
            wrapperClass,
            disable,
            environment,
        } = this.props;

        if (disable) {
            return null;
        }

        return (
            <div className={classNames(styles.wrap, wrapperClass)}>
                {fetchHeart && (
                    <div
                        className={classNames(styles.iconWrap, heartIconWrapperClass)}
                        data-tn="item-save-to-portfolio"
                    >
                        {this.renderHeart()}
                        <ServerVarsContextConsumer>
                            {({ isDesktop }) =>
                                showTooltip &&
                                isDesktop && (
                                    <React.Suspense fallback="">
                                        <TradeFavoritesTooltip
                                            openSourcingModal={this.openSourcingModal}
                                            closeTooltip={this.closeTooltip}
                                        />
                                    </React.Suspense>
                                )
                            }
                        </ServerVarsContextConsumer>
                        <PdpSharedSourcingModalLoaderWrapper
                            environment={environment}
                            itemId={itemId}
                            isOpen={isModalOpen}
                            onClose={this.closeSourcingModal}
                        />
                    </div>
                )}
                {fetchFolder && (
                    <div
                        className={classNames(styles.iconWrap, folderIconWrapperClass)}
                        data-tn="item-save-to-folder"
                    >
                        {this.renderFolder()}
                        {!isVerifiedTrade && (
                            <DesignerConnectionFolderConfirmationModal
                                isOpen={showFolderConfirmation}
                                onClose={() => {
                                    this.setState({
                                        showFolderConfirmation: false,
                                        portfolioName: '',
                                    });
                                    if (authModalLoader.isPaused()) {
                                        authModalLoader.resume();
                                    }
                                }}
                                itemTitle={itemTitle || ''}
                                portfolioName={portfolioName}
                            />
                        )}
                        {isTradeSourcingPromptModalOpen && (
                            <PdpRespTradeSourcingPromptModal
                                environment={environment}
                                itemId={itemId}
                                onClose={() =>
                                    this.setState({ isTradeSourcingPromptModalOpen: false })
                                }
                                onOpen={logTradeSourcingPromptModalView}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }
}

FavoritesItem.defaultProps = {
    fetchHeart: true,
    fetchFolder: true,
};

FavoritesItem.propTypes = {
    itemId: PropTypes.string.isRequired,
    itemImage: PropTypes.string,
    itemTitle: PropTypes.string,
    location: PropTypes.oneOf([
        locations.SIDEBAR,
        locations.SELLER_GRID,
        locations.SIMILAR_ITEM_GRID,
        locations.VISUALLY_SIMILAR_ITEM_GRID,
        locations.SEARCH_BROWSE,
        locations.ZOOM_MODAL,
        locations.VIEW_IN_ROOM,
    ]).isRequired,

    // passed from FavoritesProvider
    disable: PropTypes.bool,
    isVerifiedTrade: PropTypes.bool,
    userId: PropTypes.string,
    userIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    itemIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    environment: PropTypes.object.isRequired,
    singlePortfolioItem: PropTypes.bool.isRequired,
    loadPortfolioItemData: PropTypes.bool.isRequired,
    viewer: PropTypes.object,
    fetchHeart: PropTypes.bool.isRequired,
    fetchFolder: PropTypes.bool.isRequired,
    heartIconClass: PropTypes.string,
    heartIconWrapperClass: PropTypes.string,
    folderIconClass: PropTypes.string,
    folderIconWrapperClass: PropTypes.string,
    wrapperClass: PropTypes.string,
    showTooltipOnFirstClick: PropTypes.bool,
    tooltipTrackingLabel: PropTypes.string,
};
